import { $reportTemplates, type ReportTemplates } from '@/stores';
import { closeDialog } from '@/stores/dialog';
import { alert } from '@/utils/alertUtils';
import clsx from 'clsx';
import { useState } from 'react';

export function TemplateEditor() {
  const [editedTemplates, setEditedTemplates] = useState<ReportTemplates>(
    $reportTemplates.get(),
  );
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateContent, setNewTemplateContent] = useState('');

  const handleSave = () => {
    $reportTemplates.set(editedTemplates);
    closeDialog();
  };

  const handleCancel = () => {
    setEditedTemplates($reportTemplates.get());
    closeDialog();
  };

  const handleUpdateTemplate = (name: string, content: string) => {
    setEditedTemplates(prev => ({
      ...prev,
      [name]: content,
    }));
  };

  const handleDeleteTemplate = (name: string) => {
    setEditedTemplates(prev => {
      const newTemplates = { ...prev };
      delete newTemplates[name];
      return newTemplates;
    });
  };

  const handleAddTemplate = () => {
    if (!newTemplateName.trim()) return;

    const normalizedName = newTemplateName.trim().toUpperCase();
    setEditedTemplates(prev => ({
      ...prev,
      [normalizedName]: newTemplateContent,
    }));
    setNewTemplateName('');
    setNewTemplateContent('');
  };

  const handleExportTemplates = () => {
    const dataStr =
      'data:text/json;charset=utf-8,' +
      encodeURIComponent(JSON.stringify(editedTemplates, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute('href', dataStr);
    downloadAnchorNode.setAttribute('download', 'report_templates.json');
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleImportTemplates = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = event => {
      try {
        const importedTemplates = JSON.parse(
          event.target?.result as string,
        ) as ReportTemplates;
        setEditedTemplates(prev => ({
          ...prev,
          ...importedTemplates,
        }));
      } catch (error) {
        alert({
          title: 'Error',
          message: 'Failed to import templates: Invalid JSON file',
        });
        console.error('Failed to import templates:', error);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <div
      className="stop-scrolling fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={closeDialog}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-2xl font-semibold text-gray-900">
            Edit Report Templates
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Create and customize report templates for quick access
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Existing Templates */}
          <div className="space-y-4">
            {Object.entries(editedTemplates).map(([name, content]) => (
              <div key={name} className="rounded-lg border border-gray-300 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {name}
                  </h3>
                  <button
                    onClick={() => handleDeleteTemplate(name)}
                    className="cursor-pointer rounded px-2 py-1 text-sm font-medium text-red-600 transition-colors hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
                <textarea
                  value={content}
                  onChange={e => handleUpdateTemplate(name, e.target.value)}
                  className="min-h-30 w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Template content..."
                />
              </div>
            ))}
          </div>

          {/* Add New Template */}
          <div className="mt-6 border-t border-gray-300 pt-6">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">
              Add New Template
            </h3>
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="template-name"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Template Name (trigger word)
                </label>
                <input
                  id="template-name"
                  type="text"
                  value={newTemplateName}
                  onChange={e => setNewTemplateName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="e.g., SITREP"
                />
              </div>
              <div>
                <label
                  htmlFor="template-content"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Template Content
                </label>
                <textarea
                  id="template-content"
                  value={newTemplateContent}
                  onChange={e => setNewTemplateContent(e.target.value)}
                  className="min-h-30 w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Template content with <placeholders>..."
                />
              </div>
              <button
                onClick={handleAddTemplate}
                disabled={!newTemplateName.trim()}
                className={clsx(
                  'rounded-md px-4 py-2 font-medium',
                  newTemplateName.trim()
                    ? 'cursor-pointer bg-green-600 text-white hover:bg-green-700'
                    : 'cursor-not-allowed bg-gray-300 text-gray-500',
                )}
              >
                Add Template
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex space-x-3 border-t border-gray-200 px-6 py-4">
          <button
            onClick={handleExportTemplates}
            className="cursor-pointer rounded-md bg-yellow-600 px-4 py-2 font-medium text-white hover:bg-yellow-700"
          >
            Export Templates
          </button>
          <label
            htmlFor="import-templates"
            className="cursor-pointer rounded-md bg-indigo-600 px-4 py-2 font-medium text-white hover:bg-indigo-700"
          >
            Import Templates
            <input
              id="import-templates"
              type="file"
              accept="application/json"
              onChange={handleImportTemplates}
              className="hidden"
            />
          </label>
          <div className="grow" />
          <button
            onClick={handleCancel}
            className="cursor-pointer rounded-md border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700"
          >
            Save Templates
          </button>
        </div>
      </div>
    </div>
  );
}
